"use no memo";

import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { Status } from "@repo/database";
import { capitalize, formatIntToCurrency, highlightText } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import { PenIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { Product } from "~/providers/product-provider";
import { SettingsHeader } from "~/components/layout/settings-header";
import { DataTable } from "~/components/miscellaneous/data-table";
import { DataTableColumnHeader } from "~/components/miscellaneous/data-table-column-header";
import { Button } from "~/components/ui/button";
import { ModalInput } from "~/components/ui/modal-input";
import { Switch } from "~/components/ui/switch";
import { useProduct } from "~/hooks/use-product";
import { useProductManager } from "~/managers/use-product-manager";

export const Route = createFileRoute("/_main/settings/_layout/products/")({
  component: Products,
});

const PriceCell = ({
  product,
  globalFilter,
}: {
  product: Product;
  globalFilter: string;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const { updateProduct } = useProductManager();
  const { t } = useTranslation("common");
  return (
    <ModalInput
      label={t("Product.default_price_edit_label", {
        product_name: product.name,
      })}
      type="decimal"
      onChange={(e) => {
        if (!product.categoryId) return;
        const priceId = product.prices.at(0)?.priceId;
        if (!priceId) return;
        // TODO: make sure to only update default price
        void updateProduct({
          id: product.id,
          categoryId: product.categoryId,
          prices: [
            {
              priceId,
              value: e,
            },
          ],
        });
      }}
      value={product.prices.at(0)?.value}
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsEditing(!isEditing);
        }}
      >
        <p
          dangerouslySetInnerHTML={{
            __html:
              highlightText(
                product.prices
                  .map((price) => formatIntToCurrency(price.value, true))
                  .join(" - "),
                globalFilter,
              ) ?? "",
          }}
        />
      </button>
    </ModalInput>
  );
};

function Products() {
  const { t } = useTranslation("common");
  const { products, productCategories } = useProduct();
  const { openProductDialog, updateProductStatus } = useProductManager();

  const columns: ColumnDef<Product>[] = [
    {
      id: "actions",
      size: 100,
      cell: ({ row: { original: product } }) => {
        return (
          <div className="flex flex-row items-center gap-2">
            <Button
              onClick={() => {
                openProductDialog(product);
              }}
              className="p-1 w-8 h-8 rounded-full"
              variant="outline"
            >
              <PenIcon size={14} />
            </Button>
          </div>
        );
      },
    },
    {
      id: t("name"),
      accessorKey: "name",
      filterFn: "includesString",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("name")} />
      ),
      cell: ({ row: { original: product }, table }) => {
        const globalFilter = table.getState().globalFilter as string;

        return (
          <p
            dangerouslySetInnerHTML={{
              __html: highlightText(product.name, globalFilter) ?? "",
            }}
          ></p>
        );
      },
    },
    {
      id: t("Product.category"),
      accessorKey: "categoryName",
      filterFn: "equals",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Product.category")} />
      ),
      cell: ({ row: { original: product }, table }) => {
        const globalFilter = table.getState().globalFilter as string;
        const productCategory = productCategories.find(
          (category) => category.id === product.categoryId,
        );
        return (
          <p
            dangerouslySetInnerHTML={{
              __html:
                highlightText(productCategory?.name ?? "", globalFilter) ?? "",
            }}
          ></p>
        );
      },
    },
    {
      id: t("price"),
      accessorKey: "prices",
      filterFn: "includesString",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("price")} />
      ),
      cell: ({ row: { original: product }, table }) => {
        const globalFilter = table.getState().globalFilter as string;

        return <PriceCell product={product} globalFilter={globalFilter} />;
      },
    },
    {
      id: t("position"),
      accessorKey: "position",
      filterFn: "includesString",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("position")} />
      ),
      cell: ({ row: { original: product }, table }) => {
        const globalFilter = table.getState().globalFilter as string;

        return (
          <p
            dangerouslySetInnerHTML={{
              __html:
                highlightText(product.position.toString(), globalFilter) ?? "",
            }}
          ></p>
        );
      },
    },
    {
      id: t("status"),
      accessorKey: "status",
      size: 1,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("status")} />
      ),
      cell: ({ row: { original: product } }) => {
        return (
          <Switch
            onClick={(e) => e.stopPropagation()}
            onCheckedChange={(checked) =>
              updateProductStatus(
                product.id,
                checked ? Status.active : Status.inactive,
              )
            }
            className="data-[state=checked]:bg-green-500 data-[state=unchecked]:bg-red-500"
            checked={product.status === Status.active ? true : false}
          />
        );
      },
    },
  ];

  return (
    <>
      <SettingsHeader
        title={t("products")}
        buttonAction={() => openProductDialog()}
        buttonLabel={capitalize(t("new_product"))}
      />

      <DataTable
        defaultSorting={[{ id: t("name"), desc: false }]}
        columns={columns}
        data={products}
        filterActiveForColumns={[t("Product.category")]}
      />
    </>
  );
}
